'use client';

import { useViewCount } from '@/services/query/view.query';
import { useMemberStore } from '@/stores/member.store';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { encryptAES128 } from '@/lib/crypto-utils';
import { isAppWebView } from '@/components/analytics/analytics-host';
import {
  consumeAcademyEntryHandoff,
  getLastRecordedPathname,
  getNavigationType,
  getPreviousPathname,
  resolveEntrySource,
  startAcademyVisit,
} from '@/lib/academy-visit';
import {
  getAcademyIdFromDetailPath,
  shouldCountDetailEntry,
  shouldCountListEntry,
} from '../utils/academy-view-count-path';

/** 동일 path 진입에 대한 중복 카운트 방지 (Strict Mode 이중 실행 등). 같은 path를 이 시간 내에 다시 세지 않음. */
const VIEW_COUNT_DEBOUNCE_MS = 2000;
const lastCounted: { path: string; at: number } = { path: '', at: 0 };

function shouldSkipDuplicateCount(currentPath: string): boolean {
  const now = Date.now();
  if (currentPath !== lastCounted.path) return false;
  return now - lastCounted.at < VIEW_COUNT_DEBOUNCE_MS;
}

function markCounted(currentPath: string): void {
  lastCounted.path = currentPath;
  lastCounted.at = Date.now();
}

/**
 * 학원 상세 진입 출처 기록(academy_visit)의 중복 방지. view_count 와 같은 규칙
 * (같은 path 를 이 시간 안에 다시 세지 않음)이지만, view_count 는 hash 가 없으면 건너뛰므로
 * 기록 여부가 서로 달라 상태를 따로 둔다.
 */
const lastVisitRecorded: { path: string; at: number } = { path: '', at: 0 };

/** 같은 오리진 리퍼러의 pathname. 외부·없음·파싱 실패면 null. */
function sameOriginReferrerPath(): string | null {
  try {
    if (!document.referrer) return null;
    const url = new URL(document.referrer);
    return url.host === window.location.host ? url.pathname : null;
  } catch {
    return null;
  }
}

/** 이 문서에서 진입 판정을 이미 한 번 했는가(새로고침·새 문서 판정은 첫 판정에만 쓴다). */
let isFirstEntryCheckInDocument = true;

/**
 * 학원 상세 "진입" 1회를 출처와 함께 기록한다(POST /v1/academy-visit).
 * view_count 와 달리 **hash 와 무관하게**(비로그인 웹 포함) 보낸다.
 *
 * 직전 경로는 루트 레이아웃이 쌓는 탭 이력(`getPreviousPathname`) 또는 같은 오리진 리퍼러에서
 * 읽는다(아래 주석) — academy 레이아웃 밖에서 SPA 로 들어오면 이 레이아웃의 ref 는 비어 있다.
 * URL 쿼리(`from`·`fromPos`·`version`·`hash`)는 effect 안에서 `window.location` 으로 읽는다.
 */
function recordAcademyVisitEntry(currentPath: string): void {
  const isFirstCheck = isFirstEntryCheckInDocument;
  isFirstEntryCheckInDocument = false;
  const navigationType = isFirstCheck ? getNavigationType() : null;

  // 새로고침은 새 진입이 아니다 — 탭 이력의 마지막이 지금 경로면 같은 화면을 다시 띄운 것이고,
  // 그 전 문서에서 이미 진입을 기록했다.
  if (
    navigationType === 'reload' &&
    getLastRecordedPathname() === currentPath
  ) {
    return;
  }

  // 직전 경로를 어디서 읽나.
  //  - 같은 문서 안의 SPA 이동, 또는 뒤로·앞으로 이동: 탭 이력(sessionStorage 로 문서를 넘어
  //    이어진다). `<a href>` 전체 이동으로 간 수강료 화면에서 뒤로 오는 왕복이 여기서 걸러진다.
  //  - 새 문서 이동(주소 입력·외부 링크·전체 이동 링크): 탭 이력은 이 문서와 무관할 수 있다
  //    (같은 탭에서 주소를 쳐도 이력은 남아 있다). 그래서 같은 오리진 리퍼러만 믿는다.
  const prevPath =
    isFirstCheck && navigationType !== 'back_forward'
      ? sameOriginReferrerPath()
      : getPreviousPathname(currentPath);
  if (!shouldCountDetailEntry(prevPath ?? '', currentPath)) return;

  const academyId = Number(getAcademyIdFromDetailPath(currentPath));
  if (!Number.isInteger(academyId) || academyId <= 0) return;

  const now = Date.now();
  if (
    lastVisitRecorded.path === currentPath &&
    now - lastVisitRecorded.at < VIEW_COUNT_DEBOUNCE_MS
  ) {
    return;
  }
  lastVisitRecorded.path = currentPath;
  lastVisitRecorded.at = now;

  const query = new URLSearchParams(window.location.search);
  // 표식은 출처가 URL from 으로 정해져도 꺼내서 지운다 — 남겨 두면 30초 안에 같은 학원에
  // 다시 들어올 때 엉뚱하게 쓰인다.
  const handoff = consumeAcademyEntryHandoff(academyId, now);
  const entry = resolveEntrySource({
    academyId,
    fromParam: query.get('from'),
    fromPosParam: query.get('fromPos'),
    handoff,
    now,
    prevPath,
    isAppWebView: isAppWebView(),
    navigationType: getNavigationType(),
    referrer: document.referrer,
    currentHost: window.location.host,
  });

  const memberState = useMemberStore.getState();
  startAcademyVisit({
    academyId,
    entry,
    appVersion: query.get('version')?.trim() || memberState.version,
    hash: query.get('hash')?.trim() || memberState.hash,
  });
}

/**
 * pathname 변경 시 학원 리스트/상세 "진입" 규칙에 따라 뷰 카운트만 수행.
 * - 학원 리스트: 밖에서 리스트로 들어올 때만 1회
 * - 학원 상세: 같은 학원 하위 메뉴에서 돌아온 경우 제외, 진입 시 1회
 * - 동일 path에 대해 짧은 시간 내 재호출은 스킵 (Strict Mode 이중 실행 방지)
 */
export function useAcademyViewCount() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prevPathRef = useRef<string | null>(null);
  const storeHash = useMemberStore((s) => s.hash);
  const { mutate: viewCount } = useViewCount();

  // view-count에 넣을 location 값
  // - URL 쿼리에 이미 암호화된 값(encryptLocation)이 있으면 그대로 사용
  // - 없으면 URL의 raw location을 암호화해서 사용
  const encryptLocationParam =
    searchParams.get('encryptLocation')?.trim() ?? '';
  const rawLocationParam = searchParams.get('location')?.trim() ?? '';
  const effectiveLocation =
    encryptLocationParam ||
    (rawLocationParam ? (encryptAES128(rawLocationParam) ?? '') : '');

  const effectiveHash = (
    searchParams.get('hash')?.trim() ??
    storeHash ??
    ''
  ).trim();

  // 진입 출처 기록 — pathname 이 바뀔 때만. hash 가 늦게 채워져 아래 effect 가 다시 도는 경우에
  // 진입이 두 번 기록되지 않도록 view_count effect 와 분리한다.
  useEffect(() => {
    if (!pathname) return;
    try {
      recordAcademyVisitEntry(pathname);
    } catch (error) {
      // 집계 때문에 화면이 깨져서는 안 된다
      console.warn('[academy-visit] 진입 판정 실패', error);
    }
  }, [pathname]);

  useEffect(() => {
    const currentPath = pathname ?? '';
    const prevPath = prevPathRef.current;

    if (!effectiveHash) {
      prevPathRef.current = currentPath;
      return;
    }

    const countList = shouldCountListEntry(prevPath ?? '', currentPath);
    const countDetail = shouldCountDetailEntry(prevPath ?? '', currentPath);
    if (!countList && !countDetail) {
      prevPathRef.current = currentPath;
      return;
    }

    if (shouldSkipDuplicateCount(currentPath)) {
      prevPathRef.current = currentPath;
      return;
    }

    if (countList) {
      viewCount({
        type: '학원찾기',
        hash: effectiveHash,
        location: effectiveLocation,
      });
    }
    if (countDetail) {
      const academyId = getAcademyIdFromDetailPath(currentPath);
      if (academyId) {
        const numId = Number(academyId);
        if (Number.isFinite(numId)) {
          viewCount({
            type: '학원상세',
            academyId: numId,
            hash: effectiveHash,
            location: effectiveLocation,
          });
        }
      }
    }

    markCounted(currentPath);
    prevPathRef.current = currentPath;
  }, [pathname, effectiveHash, viewCount]);
}
